'use client';

import { useRouter } from 'next/navigation';

import { useOpenDirectMessageMutation } from '@/features/chat/api/chatApi';
import { toActiveChat } from '@/features/chat/utils/conversations';
import { getChatSearchParams } from '@/features/chat/utils/routes';
import { useAppSelector } from '@/store/hooks';
import { Avatar } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './WorkspaceMemberAvatar.module.scss';
import { WorkspaceMemberHoverCard } from './WorkspaceMemberHoverCard';

import type { ComponentProps } from 'react';

export type WorkspaceMemberAvatarUser = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  email?: string | null;
  avatar?: string | null;
  avatar_url?: string | null;
};

type WorkspaceMemberAvatarProps = {
  user: WorkspaceMemberAvatarUser;
  roleLabel?: string | null;
  size?: ComponentProps<typeof Avatar>['size'];
  canMessage?: boolean;
  closeOnTriggerMouseLeave?: boolean;
  openOnFocus?: boolean;
};

function getDisplayName(user: WorkspaceMemberAvatarUser) {
  return (
    user.display_name ||
    `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
    user.email ||
    'Unknown member'
  );
}

function getAvatarSrc(user: WorkspaceMemberAvatarUser) {
  return user.avatar_url ?? user.avatar ?? undefined;
}

export function WorkspaceMemberAvatar({
  user,
  roleLabel,
  size = 'small',
  canMessage = true,
  closeOnTriggerMouseLeave = false,
  openOnFocus = true,
}: WorkspaceMemberAvatarProps) {
  const router = useRouter();
  const currentUserEmail = useAppSelector(
    (state) => state.auth.currentUser?.email,
  );
  const currentWorkspaceSlug = useAppSelector(
    (state) => state.auth.currentWorkspaceSlug,
  );
  const [openDirectMessage] = useOpenDirectMessageMutation();
  const name = getDisplayName(user);
  const isCurrentUser =
    Boolean(currentUserEmail && user.email) &&
    currentUserEmail?.trim().toLowerCase() === user.email?.trim().toLowerCase();

  const handleMessage = async (userId: string) => {
    if (isCurrentUser || !currentWorkspaceSlug) {
      return;
    }

    try {
      const response = await openDirectMessage({
        participant_ids: [userId],
      }).unwrap();

      if (!response.data) {
        return;
      }

      const activeChat = toActiveChat(response.data, currentUserEmail);
      router.push(
        `/${currentWorkspaceSlug}/chat?${getChatSearchParams(activeChat)}`,
      );
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <span className={styles.workspaceMemberAvatar}>
      <WorkspaceMemberHoverCard
        user={user}
        roleLabel={roleLabel}
        canMessage={canMessage && !isCurrentUser}
        closeOnTriggerMouseLeave={closeOnTriggerMouseLeave}
        openOnFocus={openOnFocus}
        onMessage={(userId) => {
          void handleMessage(userId);
        }}
      >
        <Avatar
          size={size}
          name={name}
          src={getAvatarSrc(user)}
          showTooltip={false}
        />
      </WorkspaceMemberHoverCard>
    </span>
  );
}
